import { AppConfig } from '../types/app';
import { joinUrlPath } from './apiHelper';
import { shouldUseCustomDomain } from './featureEvaluator';

export type GandalfEndpoints = {
  default: string;
  customDomain?: string;
};

/**
 * Determines if the new IDPC endpoint should be used
 * @param config The application configuration
 * @returns True if the new IDPC endpoint should be used, false otherwise
 */
export function shouldUseNewIdpcEndpoint(config: AppConfig): boolean {
  return (
    config.enableNewIdpCEndpoints === 'true' && Boolean(config.newIdpcEndpoint)
  );
}

/**
 * Returns the appropriate IDPC endpoint based on configuration
 * @param config The application configuration
 * @returns The endpoint URL to use
 */
export function getIdpcEndpoint(config: AppConfig): string {
  return shouldUseNewIdpcEndpoint(config)
    ? config.newIdpcEndpoint!
    : config.gandalfIDPEndpoint;
}

/**
 * Returns the Gandalf Profile endpoints based on configuration.
 */
export function getGandalfProfileEndpoints(
  config: AppConfig
): GandalfEndpoints {
  return {
    default: config.gandalfProfileEndpoint,
    customDomain: config.gandalfProfileCustomDomainEndpoint,
  };
}

export type ResolvedUrls = {
  defaultUrl: string;
  customDomainUrl?: string;
  useCustomDomain: boolean;
};

function resolveEndpointUrls(
  endpoints: GandalfEndpoints,
  path: string,
  featureName: string,
  rolloutPercent?: string
): ResolvedUrls {
  const percent = rolloutPercent ? Number(rolloutPercent) : undefined;
  return {
    defaultUrl: joinUrlPath(endpoints.default, path),
    customDomainUrl: endpoints.customDomain
      ? joinUrlPath(endpoints.customDomain, path)
      : undefined,
    useCustomDomain: shouldUseCustomDomain(
      featureName,
      endpoints.customDomain,
      percent
    ),
  };
}

/**
 * Resolves Profile endpoint URLs and determines whether to use custom domain.
 */
export function resolveProfileUrls(
  config: AppConfig,
  path: string
): ResolvedUrls {
  return resolveEndpointUrls(
    getGandalfProfileEndpoints(config),
    path,
    'profile',
    config.profileCustomDomainRolloutPercent
  );
}
